import {ChangeDetectionStrategy, Component, inject, input, LOCALE_ID, OnInit, ViewEncapsulation} from "@angular/core";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ContactApiService} from "../../../api/contact/contact.api.service";
import {catchError, firstValueFrom, throwError} from "rxjs";
import {NgClass} from "@angular/common";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";

interface IOption {
  id: number;
  name: string;
}

@Component({
  standalone: true,
  selector: 'app-contact-us-section-component',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="font-tektur text-mainColor mb-10">
      <div class="text-6xl font-bold mb-2.5" i18n>
        Contact us
      </div>
      <div class="text-xl" i18n>
        Fill up the form, and we’ll get back to you within 24 hours
      </div>
    </div>

    <form [formGroup]="form" (ngSubmit)="submitForm()" novalidate>
      <div class="flex flex-col gap-4">
        <div [ngClass]="{'has-error': form.get('subject')?.invalid && form.get('subject')?.touched}"
             class="relative mb-5">
          <label for="disabledSelect" class="form-label font-tektur text-mainColor" i18n>
            Select type of your inquiry
          </label>
          <select
            formControlName="subject"
            id="disabledSelect"
            class="bg-transparent block w-full mt-0 px-0.5 border-0 border-b-2 border-[#8996A5] focus:ring-0 focus:border-black font-tektur"
          >
            <option [value]="null" disabled selected>Select...</option>
            @for (option of options; track option) {
              <option [value]="option.id">{{ option.name }}</option>
            }
          </select>
          @if (form.get('subject')?.invalid && form.get('subject')?.touched) {
            <div class="error-message text-rose-600 font-tektur absolute" i18n>
              Subject is required.
            </div>
          }
        </div>
        <div [ngClass]="{'has-error': form.get('name')?.invalid && form.get('name')?.touched}" class="relative mb-5">
          <label for="pages-contact_us-form-name-input" class="form-label font-tektur text-mainColor" i18n>
            Your Name
          </label>
          <input
            formControlName="name"
            type="text"
            id="pages-contact_us-form-name-input"
            class="bg-transparent mt-0 block w-full px-0.5 border-0 border-b-2 border-[#8996A5] focus:ring-0 focus:border-black font-tektur placeholder:text-[#7B8693]"
            [placeholder]="'Type'"
          />
          @if (form.get('name')?.invalid && form.get('name')?.touched) {
            <div class="error-message text-rose-600 font-tektur absolute" i18n>
              Name is required.
            </div>
          }
        </div>
        <div [ngClass]="{'has-error': form.get('email')?.invalid && form.get('email')?.touched}" class="relative mb-5">
          <label for="pages-contact_us-form-email-input" class="form-label font-tektur text-mainColor" i18n>
            Email
          </label>
          <input
            formControlName="email"
            type="email"
            id="pages-contact_us-form-email-input"
            class="bg-transparent mt-0 block w-full px-0.5 border-0 border-b-2 border-[#8996A5] focus:ring-0 focus:border-black font-tektur placeholder:text-[#7B8693]"
            [placeholder]="'Type'"
          />
          @if (form.get('email')?.invalid && form.get('email')?.touched) {
            <div class="error-message text-rose-600 font-tektur absolute" i18n>
              Valid email is required.
            </div>
          }
        </div>
        <div [ngClass]="{'has-error': form.get('message')?.invalid && form.get('message')?.touched}">
          <label for="pages-contact_us-form-message-textarea" class="form-label font-tektur text-mainColor" i18n>
            Message
          </label>
          <textarea
            formControlName="message"
            id="pages-contact_us-form-message-textarea"
            rows="3"
            class="relative bg-transparent mt-0 block w-full px-0.5 border-0 border-b-2 border-[#8996A5] focus:ring-0 focus:border-black font-tektur placeholder:text-[#7B8693]"
            [placeholder]="'Type'"
          ></textarea>
          @if (form.get('message')?.invalid && form.get('message')?.touched) {
            <div class="error-message text-rose-600 font-tektur absolute" i18n>
              Message is required.
            </div>
          }
        </div>
        <button
          type="submit"
          unselectable="on"
          onselectstart="return false;"
          onmousedown="return false;"
          [class.bg-green-100]="showSuccessAlert"
          [class.bg-[#1f2024]]="!showSuccessAlert"
          class="noselect primary-button w-full h-[69px] px-[18px] justify-center items-center gap-[7px] inline-flex transition-all
                 duration-150 ease-in-out active:scale-95 cursor-pointer mt-5"
        >
          <div
            class="send-button text-center text-[#f7f8f7] text-2xl font-medium font-['Tektur'] leading-loose
                         tracking-[2.88px] transition-all duration-150 ease-in-out active:scale-95 active:shadow-lg"
          >


            @if (showSuccessAlert) {

              <div class="text-green-700 flex gap-4 justify-center items-center">
                <span>Success!</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor"
                     class="bi bi-send-check" viewBox="0 0 16 16">
                  <path
                    d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855a.75.75 0 0 0-.124 1.329l4.995 3.178 1.531 2.406a.5.5 0 0 0 .844-.536L6.637 10.07l7.494-7.494-1.895 4.738a.5.5 0 1 0 .928.372zm-2.54 1.183L5.93 9.363 1.591 6.602z"/>
                  <path
                    d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-1.993-1.679a.5.5 0 0 0-.686.172l-1.17 1.95-.547-.547a.5.5 0 0 0-.708.708l.774.773a.75.75 0 0 0 1.174-.144l1.335-2.226a.5.5 0 0 0-.172-.686"/>
                </svg>
              </div>

            } @else {

              @if (pending) {

                <div class="animate-spin">
                  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor"
                       class="bi bi-arrow-repeat" viewBox="0 0 16 16">
                    <path
                      d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41m-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9"/>
                    <path fill-rule="evenodd"
                          d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5 5 0 0 0 8 3M3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9z"/>
                  </svg>
                </div>

              } @else {
                <span class="cursor-pointer font-tektur" i18n>SEND MESSAGE</span>

              }

            }

          </div>
        </button>
      </div>
    </form>
  `,
  imports: [
    ReactiveFormsModule,
    NgClass
  ],
  host: {
    class: 'w-full bg-white py-10 flex flex-col'
  }
})
export class ContactUsComponent implements OnInit {

  public readonly subject = input<string | null>(null);

  public options: IOption[] = [];
  public form!: FormGroup;
  public pending: boolean = false;
  public showSuccessAlert: boolean = false;
  public showErrorAlert: boolean = false;

  private readonly localeId = inject(LOCALE_ID);
  private readonly httpClient = inject(HttpClient);
  private readonly formBuilder = inject(FormBuilder);
  private readonly contactApiService = inject(ContactApiService);

  ngOnInit(): void {
    this.initOptions();
    this.initForm();
  }

  public get currentLanguageCode(): string {
    return this.localeId[0] + this.localeId[1];
  }

  public initOptions(): void {
    this.options =
      [
        {id: 0, name: $localize`:@@contactType.0:Regarding my donation`},
        {id: 1, name: $localize`:@@contactType.1:I have a car to donate`},
        {id: 2, name: $localize`:@@contactType.2:I\'d like to buy a car for you`},
        {id: 3, name: $localize`:@@contactType.3:I can help with media and promotion`},
        {id: 4, name: $localize`:@@contactType.4:Message of Support`},
        {id: 5, name: $localize`:@@contactType.5:Other`},
        {id: 6, name: $localize`:@@contactType.6:Become a fundraiser`},
      ]
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      subject: [this.subject(), [Validators.required]],
      name: [null, [Validators.required, Validators.minLength(2)]],
      email: [null, [Validators.required, Validators.email]],
      message: [null, [Validators.required, Validators.minLength(5)]],
    });
  }

  private startPending(): void {
    this.pending = true;
  }

  private stopPending(): void {
    this.pending = false;
  }

  private resetAlerts(): void {
    this.showSuccessAlert = false;
    this.showErrorAlert = false;
  }

  public async submitForm(): Promise<void> {
    this.resetAlerts();
    this.startPending();
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const translatePath = `/assets/i18n/contact-us/${this.currentLanguageCode}.json`;
      const request$ = this.httpClient.get(translatePath);
      firstValueFrom(request$).then(({form: {inquiries}}: any) => {
        const subject = inquiries[this.form.value.subject].title;
        this.contactApiService.postDocument(JSON.stringify({
          ...this.form.value,
          subject
        }))
          .pipe(
            catchError((error: HttpErrorResponse) => {
              this.stopPending();
              this.showErrorAlert = true;
              return throwError(() => error);
            })
          )
          .subscribe(() => {
            this.form.reset({
              subject: null
            });
            this.stopPending();
            this.showSuccessAlert = true;
            setTimeout(() => {
              this.showSuccessAlert = false;
            }, 3_000)
          });
      })
    } else {
      this.stopPending();
    }
  }
}

